import styled from "@emotion/styled"
import { PInlineNotification } from "@porsche-design-system/components-react"
import { spacingStaticMd } from "@porsche-design-system/components-react/emotion"
import { useCallback, useContext } from "react"
import { defineMessages, FormattedMessage, useIntl } from "react-intl"
import { commonMessages } from "src/common/messages/common.messages"
import { useCopyToClipboard } from "src/components/copyToClipboard/hooks/useCopyToClipboard"
import { ErrorHandlerContext } from "./ErrorHandlerContext"

const errorNotificationMessages = defineMessages({
	copyIdExplanation: {
		defaultMessage:
			"This Id provides useful information to find the cause for this issue. Please use it if you want to report this problem.",
		id: "error.copyIdToClipboard.explanation",
	},
	copyIdLabel: {
		defaultMessage: "Copy Error Id",
		id: "error.copyIdToClipboard.label",
	},
	copyToClipboardSuccess: {
		defaultMessage: "Copied Error Id to clipboard",
		id: "error.copyIdToClipboard.success",
	},
})

interface ErrorNotificationInlineProps {
	className?: string
	dismissButton?: boolean
}

export const ErrorNotificationInline = ({ dismissButton, ...props }: ErrorNotificationInlineProps) => {
	const context = useContext(ErrorHandlerContext)
	const { formatMessage } = useIntl()

	const setError = context?.setError

	const handleDismiss = useCallback(() => {
		setError?.(undefined)
	}, [setError])

	const copyToClipboard = useCopyToClipboard({
		successToastText: formatMessage(errorNotificationMessages.copyToClipboardSuccess),
		value: context?.error?.traceId ?? "",
	})

	if (!context?.error) {
		return null
	}

	const {
		error: { message, traceId },
	} = context

	return (
		<StyledInlineNotification
			actionIcon="copy"
			actionLabel={traceId ? formatMessage(errorNotificationMessages.copyIdLabel) : undefined}
			onAction={traceId ? copyToClipboard : undefined}
			{...props}
			state="error"
			dismissButton={dismissButton}
			onDismiss={handleDismiss}
		>
			<span slot="heading">
				<FormattedMessage {...commonMessages.error} />
			</span>
			{message}
		</StyledInlineNotification>
	)
}

const StyledInlineNotification = styled(PInlineNotification)`
	position: relative;
	margin: ${spacingStaticMd} 0;
`
